import { CalendarDayContent, SurfaceConstants } from 'owa-calendar-surface-core';
import { getAllDayWellHeight } from 'owa-calendar-surface-options-utils';
import { getGuid } from 'owa-guid';
import * as React from 'react';
import {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    ColumnItemsProvider,
    SurfaceColumnItemsProvider,
    allDayPanel,
    createGridLines,
    createHeaders,
    createColumnsWithContent,
    createConflictGroupsLayer,
    transformAllDayItem,
    SurfaceItem,
    generateOverflowItems,
    TimeStripContent,
    TimeStripHeaderContent,
    TimeMarkerContent,
    TimeMarkerTodayContent,
} from 'owa-calendar-surface-dategrid';
import { CalendarEvent, renderAsAllDay } from 'owa-calendar-types';
import compareCalendarEvents from 'owa-calendar-types/lib/utils/compareCalendarEvents';
import DayGridColumnComponent from '../components/DayGridColumnComponent';
import DayGridAllDayColumnComponent from '../components/DayGridAllDayColumnComponent';
import { OwaDate } from 'owa-datetime';

/**
 * Creates content for mounting in calendar day (week) view
 * @param store the calendar surface store
 * @param config the configuration for calendar day/week view
 * @param maxAllDayRowsToShow the maximum all day items to be displayed
 * @param highlightedEventId the event id that is highlighted in the grid
 * @returns structured content for day/week view
 */
export function createDayGridContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    maxAllDayRowsToShow?: number,
    highlightedEventId?: string,
    customElementDecorator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element,
    createCustomElements?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider,
        isAllDay: boolean
    ) => JSX.Element[],
    customCalendarDayContentGenerator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider
    ) => Partial<CalendarDayContent>,
    customColumnContent?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        index: number
    ) => JSX.Element[],
    customItemFilter?: (item: CalendarEvent) => boolean
): CalendarDayContent {
    const columnProvider = config.columnProvider;

    let columnItemsProvider = new SurfaceColumnItemsProvider(
        store,
        columnProvider,
        () =>
            store.calendarItems.filter(item => {
                return customItemFilter ? customItemFilter(item) : item;
            }),
        config.supportExpandableAllDayWell(),
        maxAllDayRowsToShow,
        createEventComparator(highlightedEventId),
        getAllDayWellHeight()
    );

    return {
        gridLines: () => createGridLines(store, config),
        headers: () => createHeaders(store, config),
        content: () =>
            createCalendarInDayContent(
                columnItemsProvider,
                store,
                config,
                customElementDecorator,
                createCustomElements,
                customColumnContent
            ),
        timeStripContent: () => createCalendarTimeStripContent(store, config),
        timeStripHeaderContent: () => createCalendarTimeStripHeaderContent(store, config),
        allDayContent: () =>
            createAllDayPanel(
                columnItemsProvider,
                store,
                config,
                customElementDecorator,
                createCustomElements
            ),
        tabOrderProvider: () => [...columnItemsProvider.tabOrder],
        allDayContentProperties: () => ({
            visibleRowCount: columnItemsProvider.visibleRowCount,
            allDayUncollapsedRowCount: columnItemsProvider.allDayUncollapsedRows.length,
            allDayRowCount: columnItemsProvider.allDayRows.length,
        }),
        ...customCalendarDayContentGenerator?.(store, config, columnItemsProvider),
    };
}

/**
 * Creates the calendar surface content
 * @param items surface items collection
 * @param allDayModels collection of all day items
 * @param store surface store
 * @param config the configuration for calendar day/week view
 * @returns collection JSX elements of surface items
 */
export function createCalendarInDayContent(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    customElementDecorator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element,
    createCustomElements?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider,
        isAllDay: boolean
    ) => JSX.Element[],
    customColumnContent?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        index: number
    ) => JSX.Element[]
): JSX.Element[] {
    let columnElements = createColumnsWithContent(
        config.columnProvider,
        (columnModel, index) => {
            return [
                ...createConflictGroupsLayer(
                    cip.columnInDayItems[index],
                    store,
                    config,
                    index,
                    (element, groupIndex, item) => {
                        if (customElementDecorator) {
                            return customElementDecorator(store, config, element, groupIndex, item);
                        } else {
                            return element;
                        }
                    }
                ),
                ...(customColumnContent?.(store, config, index) || []),
            ];
        },
        (columnModel, index) => {
            return {
                component: DayGridColumnComponent,
                additionalProps: {
                    store: store,
                    config: config,
                    index: index,
                    date: columnModel as OwaDate,
                },
            };
        }
    );

    if (config.shouldShowCurrentTime?.()) {
        columnElements.push(...createTimeMarkerContent(config));
    }

    if (createCustomElements) {
        columnElements.push(...createCustomElements(store, config, cip, false));
    }

    return columnElements;
}

/**
 * Creates all day panel with content
 * @param cip The column items provider
 * @param store The calendar surface store
 * @param config The surface transform config
 */
export function createAllDayPanel(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    customElementDecorator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element,
    createCustomElements?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider,
        isAllDay: boolean
    ) => JSX.Element[]
): JSX.Element[] {
    return [
        ...createCalendarAllDayColumns(config),
        allDayPanel(getGuid(), cip, () => {
            return createCalendarAllDayContent(
                cip,
                store,
                config,
                customElementDecorator,
                createCustomElements
            );
        }),
    ];
}

function createCalendarAllDayColumns(config: CalendarSurfaceTransformProps): JSX.Element[] {
    return createColumnsWithContent(
        config.columnProvider,
        () => null,
        columnModel => ({
            component: DayGridAllDayColumnComponent,
            additionalProps: {
                date: columnModel as OwaDate,
                disableWorkingHours: config.disableWorkingHours,
            },
        })
    );
}

/**
 * Creates the content (JSX elements) for the all day well
 * @param cip The column items provider
 * @param store The surface store
 * @param config the configuration for calendar day/week view
 * @returns An array of JSX elements with all components for the all-day well
 */
function createCalendarAllDayContent(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    customElementDecorator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element,
    createCustomElements?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider,
        isAllDay: boolean
    ) => JSX.Element[]
): JSX.Element[] {
    let result: JSX.Element[] = [];

    const transformItem = (item: SurfaceItem, rowIndex: number): JSX.Element => {
        const transformedItem = transformAllDayItem(
            store,
            config.columnProvider,
            config.getItemConfig(item.model),
            item,
            rowIndex
        );

        if (customElementDecorator) {
            return customElementDecorator(store, config, transformedItem, undefined, item);
        } else {
            return transformedItem;
        }
    };

    cip.allDayRows.forEach((rowItems, rowIndex) => {
        result.push(
            ...rowItems.map(item => {
                return transformItem(item, rowIndex);
            })
        );
    });

    if (config.supportExpandableAllDayWell()) {
        let hiddenItems = cip.allDayHiddenItems;

        result.push(
            ...generateOverflowItems(
                store,
                config,
                hiddenItems,
                transformItem,
                config.columnProvider,
                0 /* always only day in view */,
                cip.allDayRows.length,
                SurfaceConstants.AllDayRowHeight,
                SurfaceConstants.AllDayRowHeight * cip.allDayRows.length,
                0 // no additional padding
            )
        );
    }

    if (createCustomElements) {
        result.push(...createCustomElements(store, config, cip, true));
    }

    return result;
}

function createEventComparator(
    highlightedEventId: string | undefined
): (item1: CalendarEvent, item2: CalendarEvent) => number {
    return (item1: CalendarEvent, item2: CalendarEvent) => {
        // For all day events, move the event to the front if it is highlighted in the grid
        if (renderAsAllDay(item1) && renderAsAllDay(item2)) {
            if (item1.ItemId.Id === highlightedEventId) {
                return -1;
            } else if (item2.ItemId.Id === highlightedEventId) {
                return 1;
            }
        }

        // Apply default sorting for non-all day or non-highlighted events
        return compareCalendarEvents(item1, item2);
    };
}

function createCalendarTimeStripContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    return [
        <TimeStripContent
            key={'timeStripContent'}
            surfaceStore={store}
            itemsLayout={config.itemsLayout}
            timeZones={store.timeZoneIds}
            timeStripConfig={config.timeStripConfig}
        />,
    ];
}

function createCalendarTimeStripHeaderContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    return [
        <TimeStripHeaderContent
            key={'timeStripHeaderContent'}
            timeZones={store.timeZoneIds}
            timeStripConfig={config.timeStripConfig}
        />,
    ];
}

function createTimeMarkerContent(config: CalendarSurfaceTransformProps): JSX.Element[] {
    return [
        <TimeMarkerContent columnProvider={config.columnProvider} key="timeMarker" />,
        <TimeMarkerTodayContent columnProvider={config.columnProvider} key="timeMarkerToday" />,
    ];
}
